import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Edit3, Save, X } from "lucide-react";
import { listTemplates, createTemplate, updateTemplate, deleteTemplate } from "@/lib/contracts.functions";

export const Route = createFileRoute("/admin_/contracts/templates")({
  ssr: false,
  head: () => ({ meta: [{ title: "Contract templates — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  beforeLoad: requireAdmin,
  component: TemplatesPage,
});

const TYPES: [string, string][] = [
  ["independent_contractor", "Independent Contractor"],
  ["therapist_agreement", "Therapist"],
  ["clinical_director_agreement", "Clinical Director"],
  ["employee", "Employee"],
  ["volunteer", "Volunteer"],
  ["partnership", "Partnership"],
  ["nda", "NDA"],
  ["custom", "Custom"],
];

function TemplatesPage() {
  const list = useServerFn(listTemplates);
  const create = useServerFn(createTemplate);
  const update = useServerFn(updateTemplate);
  const del = useServerFn(deleteTemplate);
  const qc = useQueryClient();

  const { data } = useQuery({ queryKey: ["contract-templates"], queryFn: () => list() });

  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; type: string; description: string; body: string }>({
    name: "", type: "independent_contractor", description: "", body: "[]",
  });
  const [creating, setCreating] = useState(false);

  const startEdit = (t: any) => {
    setEditing(t.id);
    setDraft({
      name: t.name, type: t.type, description: t.description ?? "",
      body: JSON.stringify(t.body ?? [], null, 2),
    });
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      let body;
      try { body = JSON.parse(draft.body); } catch { throw new Error("Body must be valid JSON: [{heading, body}]"); }
      if (editing) return update({ data: { id: editing, changes: { name: draft.name, type: draft.type as any, description: draft.description || null, body, default_terms: {}, is_active: true } } });
      return create({ data: { name: draft.name, type: draft.type as any, description: draft.description || null, body, default_terms: {}, is_active: true } });
    },
    onSuccess: () => { toast.success("Saved"); setEditing(null); setCreating(false); qc.invalidateQueries({ queryKey: ["contract-templates"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["contract-templates"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <Link to="/admin/contracts" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to contracts
        </Link>

        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl">Contract templates</h1>
            <p className="mt-1 text-sm text-muted-foreground">Reusable contract structures. Templates seed new contracts with default clauses.</p>
          </div>
          <button
            onClick={() => { setCreating(true); setEditing(null); setDraft({ name: "", type: "independent_contractor", description: "", body: "[]" }); }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> New template
          </button>
        </div>

        {(creating || editing) && (
          <div className="mb-6 rounded-xl border border-border bg-card p-5">
            <h2 className="mb-3 font-display">{editing ? "Edit template" : "New template"}</h2>
            <div className="space-y-3">
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Template name" className="w-full rounded border border-border bg-background px-3 py-2 text-sm" />
              <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} className="w-full rounded border border-border bg-background px-3 py-2 text-sm">
                {TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Short description (optional)" className="w-full rounded border border-border bg-background px-3 py-2 text-sm" />
              <div>
                <label className="text-sm font-medium">Body (JSON array of {"{heading, body}"})</label>
                <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="mt-1 w-full min-h-[240px] rounded border border-border bg-background px-3 py-2 font-mono text-xs" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
                  <Save className="h-4 w-4" /> {saveMut.isPending ? "Saving…" : "Save"}
                </button>
                <button onClick={() => { setEditing(null); setCreating(false); }} className="inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm">
                  <X className="h-4 w-4" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {(data?.items ?? []).map((t: any) => (
            <div key={t.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
              <div>
                <p className="font-medium">{t.name}</p>
                <p className="text-xs text-muted-foreground">
                  {TYPES.find(([k]) => k === t.type)?.[1] ?? t.type}
                  {t.description ? ` · ${t.description}` : ""}
                </p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(t)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><Edit3 className="h-4 w-4" /></button>
                <button onClick={() => { if (confirm(`Delete template "${t.name}"?`)) delMut.mutate(t.id); }} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {(data?.items ?? []).length === 0 && (
            <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No templates yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
